/**
 * CommandPalette — Cmd+K / Ctrl+K quick switcher.
 * Navigation, live campaign search, and quick actions in one place.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, List, BarChart3, MousePointer, DollarSign, Wrench, Users,
  Plus, RefreshCw, Sun, Moon, LogOut, Search, CornerDownLeft, Rocket,
  ShieldCheck, SlidersHorizontal,
} from 'lucide-react';
import { getCampaigns, reloadCache } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';

const PAGES = [
  { label: 'Dashboard', to: '/', icon: LayoutDashboard, hint: 'Overview' },
  { label: 'Campaigns', to: '/campaigns', icon: List, hint: 'Campaign list' },
  { label: 'Stats & Reports', to: '/stats', icon: BarChart3, hint: 'Statistics' },
  { label: 'Click Logs', to: '/clicks', icon: MousePointer, hint: 'Click logs' },
  { label: 'Conversions', to: '/conversions', icon: DollarSign, hint: 'Conversions' },
  { label: 'Tools', to: '/tools', icon: Wrench, hint: 'Tools' },
  { label: 'IP Whitelist', to: '/tools/ip-whitelist', icon: ShieldCheck, hint: 'Tools' },
  { label: 'API Keys', to: '/tools/api-keys', icon: SlidersHorizontal, hint: 'Tools' },
  { label: 'Users', to: '/users', icon: Users, hint: 'Administracja', adminOnly: true },
];

function score(text, q) {
  const t = text.toLowerCase();
  const ql = q.toLowerCase();
  if (!ql) return 1;
  if (t === ql) return 100;
  if (t.startsWith(ql)) return 80;
  if (t.includes(ql)) return 50;
  // subsequence match ("dpb" -> "dashboard pro")
  let i = 0;
  for (const ch of t) if (ch === ql[i]) i++;
  return i === ql.length ? 20 : 0;
}

export default function CommandPalette({ open, onClose }) {
  const navigate = useNavigate();
  const toast = useToast();
  const { user, logout, isAdmin } = useAuth();
  const [query, setQuery] = useState('');
  const [index, setIndex] = useState(0);
  const [campaigns, setCampaigns] = useState([]);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setIndex(0);
    setTimeout(() => inputRef.current?.focus(), 30);
    getCampaigns()
      .then((res) => setCampaigns(res.data || []))
      .catch(() => setCampaigns([]));
  }, [open]);

  const items = useMemo(() => {
    const out = [];

    // Pages
    PAGES.filter((p) => !p.adminOnly || isAdmin).forEach((p) => {
      out.push({
        id: `page-${p.to}`,
        group: 'Nawigacja',
        label: p.label,
        hint: p.hint,
        icon: p.icon,
        run: () => navigate(p.to),
      });
    });

    // Actions
    out.push(
      { id: 'act-new', group: 'Akcje', label: 'New campaign', hint: 'Create campaign', icon: Plus, run: () => navigate('/campaigns/new') },
      {
        id: 'act-cache', group: 'Akcje', label: 'Reload campaign cache', hint: 'Reload RAM cache', icon: RefreshCw,
        run: async () => {
          try { await reloadCache(); toast.success('Cache reloaded'); }
          catch (e) { toast.error('Error: ' + (e.response?.data?.error || e.message)); }
        },
      },
      {
        id: 'act-theme', group: 'Akcje', label: 'Toggle theme', hint: 'Dark / Light', icon: Sun,
        run: () => {
          const el = document.documentElement;
          const next = el.getAttribute('data-theme') === 'dark' ? 'light' : 'dark';
          el.setAttribute('data-theme', next);
          localStorage.setItem('tds-theme', next);
          // notify Shell listeners
          window.dispatchEvent(new CustomEvent('tds-theme-change', { detail: next }));
        },
      },
      { id: 'act-logout', group: 'Akcje', label: 'Log out', hint: user?.email || '', icon: LogOut, run: () => { logout(); navigate('/login'); } },
    );

    // Campaigns
    campaigns.forEach((c) => {
      out.push({
        id: `camp-${c.id}`,
        group: 'Campaigns',
        label: c.name,
        hint: `/${c.slug}`,
        icon: Rocket,
        run: () => navigate(`/campaigns/${c.id}`),
      });
    });

    if (!query.trim()) {
      // default view: pages + actions only
      return out.filter((i) => i.group !== 'Campaigns');
    }

    return out
      .map((i) => ({ ...i, _score: Math.max(score(i.label, query), score(i.hint || '', query) * 0.6) }))
      .filter((i) => i._score > 0)
      .sort((a, b) => b._score - a._score)
      .slice(0, 24);
  }, [query, campaigns, isAdmin, navigate, logout, toast, user]);

  useEffect(() => setIndex(0), [query]);

  // keep selection visible
  useEffect(() => {
    const el = listRef.current?.querySelector('.is-selected');
    el?.scrollIntoView({ block: 'nearest' });
  }, [index]);

  if (!open) return null;

  const run = (item) => {
    if (!item) return;
    onClose();
    // let palette close before heavy action
    setTimeout(() => item.run(), 20);
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setIndex((i) => Math.min(i + 1, items.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      run(items[index]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    } else if (e.key === 'Tab') {
      e.preventDefault();
      setIndex((i) => (i + 1) % Math.max(items.length, 1));
    }
  };

  let lastGroup = null;
  return (
    <div className="v2-palette-backdrop" onMouseDown={onClose}>
      <div className="v2-palette" onMouseDown={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="v2-palette__search">
          <Search size={16} />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search pages, campaigns, actions…"
            spellCheck={false}
            autoComplete="off"
          />
          <span className="v2-kbd">esc</span>
        </div>
        <div className="v2-palette__list" ref={listRef}>
          {items.length === 0 && (
            <div className="v2-palette__empty">No results for "{query}"</div>
          )}
          {items.map((item, i) => {
            const showGroup = item.group !== lastGroup;
            lastGroup = item.group;
            const Icon = item.icon;
            return (
              <div key={item.id}>
                {showGroup && <div className="v2-palette__group">{item.group}</div>}
                <button
                  type="button"
                  className={`v2-palette__item ${i === index ? 'is-selected' : ''}`}
                  onMouseEnter={() => setIndex(i)}
                  onClick={() => run(item)}
                >
                  <Icon size={15} />
                  <span className="v2-palette__label">{item.label}</span>
                  {item.hint && <span className="v2-palette__hint">{item.hint}</span>}
                  {i === index && <CornerDownLeft size={13} className="v2-palette__enter" />}
                </button>
              </div>
            );
          })}
        </div>
        <div className="v2-palette__foot">
          <span><span className="v2-kbd">↑</span> <span className="v2-kbd">↓</span> select</span>
          <span><span className="v2-kbd">↵</span> open</span>
          <span className="v2-spacer" />
          <span>{items.length} results</span>
        </div>
      </div>
    </div>
  );
}
